import { Injectable, Injector } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractRender } from 'src/app/abstract/render.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { Logger } from 'src/app/core/logger';
import { WidgetsInputModel } from 'src/app/models/widgets/widgets-input.model';
import { numericInputValidator } from 'src/app/validators/numeric-input.validator';
import { CloneService } from '../clone/clone.service';
import { WidgetRelatedInputComponent } from 'src/app/components/widgets/widget-related-input/widget-related-input.component';
import { Config } from 'src/app/config';
import { enforceNumericValidator } from 'src/app/validators/enforce-numeric.validator';
import { enforceAlphabeticValidator } from 'src/app/validators/enforce-alphabetic.validator';
import { AbstractControl } from '@angular/forms';
@Injectable({
  providedIn: 'root',
})
export class RenderInputService extends AbstractRender {
  constructor(private injector: Injector, private logger: Logger) {
    super();
  }

  /**
   * Recursive render method
   *
   * @param widget A layout node
   * @param config Formly field config array
   * @param widgetRoot Root of the layout
   * @param formlyRoot Root of the FormlyFieldConfig
   * @param isInsideQuestion True if we are rendering a subtree inside a question
   * @param isRecursive True if we want to traverse the tree to the bottom. False if we just want to render a node.
   */
  render(
    widget: WidgetsAbstract,
    config: FormlyFieldConfig[],
    widgetRoot: WidgetsAbstract,
    formlyRoot: FormlyFieldConfig,
    isInsideQuestion: boolean,
    isRecursive: boolean
  ) {
    const input = widget as WidgetsInputModel;

    // A checkbox primitive has a label (optional), a multicheckbox and an other input (optional)
    let fieldGroupPrimitive: FormlyFieldConfig = {};
    let fieldInput: FormlyFieldConfig = {
      key: input.key,
    };
    const fieldLabel: FormlyFieldConfig = {};
    let fieldPrefix: FormlyFieldConfig = {};
    let fieldSuffix: FormlyFieldConfig = {};
    let fieldCommentInput: FormlyFieldConfig = {};

    // Mix incoming template with the group
    if (input.template) {
      // Handling for old question template contains templateOptions
      input.template = input.template.replace('templateOptions', 'props');
      const modelTemplate = JSON.parse(input.template);
      fieldGroupPrimitive = Object.assign(
        {},
        fieldGroupPrimitive,
        modelTemplate
      );
      // fieldInput = Object.assign({}, fieldInput, modelTemplate);
    }

    if (input.fieldTemplate) {
      const fieldTemplate = JSON.parse(input.fieldTemplate);
      fieldInput = Object.assign({}, fieldInput, fieldTemplate);
    }

    // Mix created input with widget template
    if (input.template) {
      const modelTemplate = JSON.parse(input.template);
      fieldInput = { ...modelTemplate, ...fieldInput };
      // Deep merge not working... Merge props manually
      const mergedprops = {
        ...fieldInput.props,
        ...modelTemplate.props,
      };
      fieldInput.props = mergedprops;
    }

    // 1. Label Field

    // Override labels on Fields
    // Go up to custom model and overrides labels
    const labelOverrides: Map<string, string> = new Map<string, string>();
    CloneService.getLabelOverrides(input, widgetRoot, labelOverrides);
    const overridedString = CloneService.overrideText(
      input.label,
      labelOverrides
    );
    if (input.label) {
      fieldLabel.template = overridedString;
    }

    //  config.push(fieldLabel)

    // 2. Prefix
    if (input.prefixText && input.prefixText !== '') {
      fieldPrefix = {
        template: '<span>' + input.prefixText + '</span>',
      };
    }

    // 3. Input
    fieldInput.type = 'uw-input';
    if (!fieldInput.props) {
      fieldInput.props = {};
    }
    fieldInput.props.attributes = {
      onpaste: 'return ' + input.isPasteEnabled + ';',
    };

    //Adding Comment to input field
    if (input.commentKey) {
      fieldInput.props.commentKey = input.commentKey;

      fieldCommentInput = {
        key: input.commentKey,
        type: 'uw-input',
        validators: { validation: ['trimValidation'] },
        props: {
          label: 'Comment',
          maxLength: Config.CONSTANTS.commentMaxLength,
          required: (input.commentFieldOnOptional !== undefined &&
            input.commentFieldOnOptional === true) ? false : true,
        },
      };
    }

    // Only update validateOnBlur if not addressed in template
    if (!fieldInput.modelOptions || !fieldInput.modelOptions.updateOn) {
      fieldInput.modelOptions = {};
      if (input.validateOnBlur) {
        fieldInput.modelOptions.updateOn = 'blur';
      } else {
        fieldInput.modelOptions.updateOn = 'change';
      }
    }

    if (input.isNumeric) {
      fieldInput.props.type = 'number';
      fieldInput.props.attributes.inputmode = 'numeric';
      // fieldInput.props.keypress = (field, event) =>
      //   numericInputValidator(field, event);
      fieldInput.props.keyEnforce = 'numericInput';
      if (input.readonly) {
        fieldInput.props.readonly = input.readonly;
      }

      // Validations for current year
      if (input.isMinCurrentYear) {
        fieldInput.validators = {
          validation: ['minCurrentYear'],
        };
      } else if (input.minNum !== undefined) {
        fieldInput.props.min = input.minNum;
      }
      if (input.isMaxCurrentYear) {
        fieldInput.validators = {
          validation: ['maxCurrentYear'],
        };
      } else if (input.maxNum !== undefined && !input.isDecimal) {
        fieldInput.props.max = input.maxNum;
      }
      if (input.isDecimal) {
        fieldInput.props.isDecimal = input.isDecimal;

        if (fieldInput.validators?.validation && fieldInput.validators?.validation?.length > 0) {

          const mindecimalValidator = {
            name: 'mindecimalValidator',
            options:
              input.isInclusive && input.isInclusive === true
                ? { inclusive: true }
                : undefined,
          }
          const maxdecimalValidator =
          {
            name: 'maxdecimalValidator',
            options:
              input.isInclusive && input.isInclusive === true
                ? { inclusive: true }
                : undefined,
          }
          let length = fieldInput.validators?.validation.length;
          fieldInput.validators?.validation.push(mindecimalValidator, maxdecimalValidator);

          if (input.isInclusive && input.isInclusive === true) {
            fieldInput.props.isInclusive = input.isInclusive;
            fieldInput.validators.validation[length].options = {};
            fieldInput.validators.validation[length].options.inclusive = true;
            fieldInput.validators.validation[length + 1].options = {};
            fieldInput.validators.validation[length + 1].options.inclusive = true;
          }
          
          fieldInput.props.maxDecimalNum = input.maxNum;

        } else {
          fieldInput.validators = {
            validation: [
              {
                name: 'mindecimalValidator',
                options:
                  input.isInclusive && input.isInclusive === true
                    ? { inclusive: true }
                    : undefined,
              },
              {
                name: 'maxdecimalValidator',
                options:
                  input.isInclusive && input.isInclusive === true
                    ? { inclusive: true }
                    : undefined,
              },
            ],
          };
          if (input.isInclusive && input.isInclusive === true) {
            fieldInput.props.isInclusive = input.isInclusive;
            fieldInput.validators.validation[0].options = {};
            fieldInput.validators.validation[0].options.inclusive = true;
            fieldInput.validators.validation[1].options = {};
            fieldInput.validators.validation[1].options.inclusive = true;
          }
         
          fieldInput.props.maxDecimalNum = input.maxNum;
        }
      }

      if (input.isPercentage) {
        fieldInput.props.isPercentage = input.isPercentage;
        fieldInput.validators = {
          validation: ['percentage'],
        };
      }

      if (input.maxTextLength) {
        // fieldInput.props.maxLength = input.maxTextLength;
        const maxFunction =
          '{ if (this.value.length >' +
          input.maxTextLength +
          ') { this.value = this.value.slice(0, ' +
          input.maxTextLength +
          '); } }';
        if (!fieldInput.props.attributes) {
          fieldInput.props.attributes = {};
        }
        fieldInput.props.attributes.onInput = maxFunction;
        fieldInput.props.maxLength = input.maxTextLength;
      }
      if (input.minTextLength) {
        fieldInput.props.minLength = input.minTextLength;
      }
    } else {
      // This is for text
      if (input.pattern) {
        fieldInput.props.pattern = input.pattern;
      }

      if (input.minTextLength) {
        fieldInput.props.minLength = input.minTextLength;
      }
      if (input.maxTextLength) {
        fieldInput.props.maxLength = input.maxTextLength;
      }

      if (!fieldInput.validators) {
        fieldInput.validators = { validation: ['trimValidation'] };
      } else if (!fieldInput.validators.validation) {
        fieldInput.validators.validation = ['trimValidation'];
      } else if (!fieldInput.validators.validation.includes('trimValidation')) {
        fieldInput.validators.validation.push('trimValidation');
      }
    }

    for (const validation of input.children) {
      try {
        const valTemplate: string = validation.getTemplate();
        if (valTemplate) {
          const template = JSON.parse(valTemplate);

          // FIXME: Check if validation is only for field or for form...
          if (template.name === 'date' || template.name === 'timeRange') {
            // else if it is for whole form...
            const validators = { validators: { validation: [template] } };

            // FIXME: For now apply at the root level for date validation
            formlyRoot.validators = { validation: [template] };
            // rootValidation.push( template );
          } else {
            // Else apply at the field level
            fieldInput.validators = {
              validation: [template],
            };
          }
        }
      } catch (err) {
        this.logger.warn('validation exeception:' + err);
      }
    }

    if (input.description) {
      fieldInput.props.description = input.description;
    }
    if (input.required) {
      fieldInput.props.required = input.required;
    }
    // 4. Suffix
    if (input.suffixText && input.suffixText !== '') {
      fieldSuffix = {
        template: '<span>' + input.suffixText + '</span>',
      };
    }

    if (input.commentFieldKeys && input.commentFieldKeys.length > 0) {
      let temp = '';
      for (let i = 0; i < input.commentFieldKeys.length; i++) {
        temp +=
          '(model.' +
          input.commentFieldKeys[i] +
          ' == undefined || model.' +
          input.commentFieldKeys[i] +
          ' == "")';
        if (i != input.commentFieldKeys.length - 1) {
          temp += ' && ';
        }
      }
      fieldCommentInput = {
        key: input.key + Config.CONSTANTS.commentKeySuffix,
        type: 'uw-input',
        validators: { validation: ['trimValidation'] },
        expressions: {
          hide: '(' + temp + ' ? false : true)',
        },
        props: {
          label: 'Comment',
          maxLength: Config.CONSTANTS.commentMaxLength,
          required: true,
        },
      };
    }
    // 5. Prepare groups
    fieldGroupPrimitive.fieldGroupClassName = 'primitive-input';
    fieldGroupPrimitive.fieldGroup = [];

    // 6. Render
    fieldLabel.className = 'prop-label';
    fieldGroupPrimitive.fieldGroup?.push(fieldLabel);
    if (input.prefixText && input.prefixText !== '') {
      fieldPrefix.className = 'prop-prefix';
      fieldGroupPrimitive.fieldGroup?.push(fieldPrefix);
    }
    if (input.commentFieldKeys && input.commentFieldKeys.length > 0) {
      fieldCommentInput.className = 'prop-comment-input';
      fieldGroupPrimitive.fieldGroup?.push(fieldCommentInput);
    } else {
      fieldInput.className = 'prop-input';
      fieldGroupPrimitive.fieldGroup?.push(fieldInput);
    }
    if (input.suffixText && input.suffixText !== '') {
      fieldSuffix.className = 'prop-suffix';
      fieldGroupPrimitive.fieldGroup?.push(fieldSuffix);
    }
    if (input.commentKey) {
      fieldCommentInput.className = 'prop-comment-input';
      fieldGroupPrimitive.fieldGroup?.push(fieldCommentInput);
    }

    // Wrapper For Formly Group
    fieldGroupPrimitive.wrappers = ['field'];

    // 7. Push the group to the formly structure
    config.push(fieldGroupPrimitive);
  }
}
